import { Fragment, ReactNode } from 'react';
import styled from 'styled-components';
import Anchor from '../components/designsystem/Anchor';

// The capture group makes split() keep the urls in the result, on the odd indexes.
const URL_PATTERN = /(https?:\/\/\S+)/g;
const TRAILING_PUNCTUATION = /[.,;:!?]+$/;
const ELLIPSIS = '…';

const countOccurrences = (value: string, char: string) => value.split(char).length - 1;

const parseUrl = (url: string) => {
    try {
        return new URL(url);
    } catch {
        return null;
    }
};

const decodeSegment = (segment: string) => {
    try {
        return decodeURIComponent(segment);
    } catch {
        return segment;
    }
};

/** Sentence punctuation isn't part of the url, and neither is an unbalanced closing paren. */
export const trimUrl = (url: string) => {
    let value = url.replace(TRAILING_PUNCTUATION, '');

    while (value.endsWith(')') && countOccurrences(value, ')') > countOccurrences(value, '(')) {
        value = value.slice(0, -1).replace(TRAILING_PUNCTUATION, '');
    }

    return value;
};

/** Query strings are mostly tracking params, so the label shows the host + last path segment only. */
export const getUrlLabel = (url: string) => {
    const parsed = parseUrl(url);
    if (!parsed) {
        return url;
    }
    const host = parsed.host.replace(/^www\./, '');
    const segments = parsed.pathname.split('/').filter(Boolean).map(decodeSegment);
    if (segments.length === 0) {
        return host;
    }

    return `${host}/${segments.length > 1 ? `${ELLIPSIS}/` : ''}${segments[segments.length - 1]}`;
};

/**
 * Turns the urls within a plain text string into links, showing a shortened label instead of the full url.
 * Everything else is returned as plain text, so react keeps escaping it.
 */
const linkifyText = (text: string): ReactNode =>
    text.split(URL_PATTERN).map((part, index) => {
        if (index % 2 === 0) {
            return part;
        }
        const href = trimUrl(part);

        return (
            <Fragment key={index}>
                <UrlAnchor as="a" href={href} target="_blank" rel="noreferrer noopener" title={href}>
                    {getUrlLabel(href)}
                </UrlAnchor>
                {part.slice(href.length)}
            </Fragment>
        );
    });

export default linkifyText;

const UrlAnchor = styled(Anchor)`
    overflow-wrap: break-word;
`;
